import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { namedOperations } from '@sorare/core/src/__generated__/operations';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { SelectionSummary } from '@sorare/core/src/components/layout/SelectionSummary';
import { useSendUserEngagementIncrement } from '@sorare/core/src/hooks/useSendUserEngagementIncrement';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { UserEngagementScoreIncrement_Source } from '@sorare/core/src/protos/webview/channel/native_messages';

import { SealCardsDialogWithFetch } from '@sorare/marketplace/src/components/sealing/SealCardsDialog/index.withFetch';
import { SealingRecapWithFetch } from '@sorare/marketplace/src/components/sealing/SealingRecap/index.withFetch';
import { useSealingMutation } from '@sorare/marketplace/src/components/sealing/useSealingMutation';

type Props = {
  rarity: Rarity;
  onCancel: () => void;
  onConfirm: () => void;
  selectedCardsSlugs: Nullable<string[]>;
};

export const CardsSealingSummary = ({
  rarity,
  onCancel,
  onConfirm,
  selectedCardsSlugs,
}: Props) => {
  const { sendUserEngagementIncrement } = useSendUserEngagementIncrement();
  const [openPreSealingDialog, setOpenPreSealingDialog] = useState(false);
  const [openPostSealingRecap, setOpenPostSealingRecap] = useState(false);

  const [sealCards, { loading: sealing }] = useSealingMutation();

  const sealCardsMutation = async () => {
    await sealCards({
      variables: {
        input: {
          cardSlugs: selectedCardsSlugs || [],
        },
      },
      refetchQueries: [namedOperations.Query.RarityVaultBannerQuery],
    });
  };

  const maxCardsPerBatch = 100;
  const hasReachedMaxCardsPerBatch = Boolean(
    selectedCardsSlugs?.length && selectedCardsSlugs.length > maxCardsPerBatch
  );

  return (
    <>
      <SealCardsDialogWithFetch
        open={openPreSealingDialog}
        rarity={rarity}
        sealing={sealing}
        onClose={() => setOpenPreSealingDialog(false)}
        onConfirm={async () => {
          await sealCardsMutation();
          setOpenPreSealingDialog(false);
          setOpenPostSealingRecap(true);

          sendUserEngagementIncrement(
            35,
            UserEngagementScoreIncrement_Source.SEAL_CARD
          );
        }}
        selectedCardsSlugs={selectedCardsSlugs || []}
      />
      {!!openPostSealingRecap && (
        <SealingRecapWithFetch
          rarity={rarity}
          selectedCardsSlugs={selectedCardsSlugs || []}
          onClose={() => {
            setOpenPostSealingRecap(false);
            onConfirm();
          }}
        />
      )}
      <SelectionSummary
        nbCardsSelected={selectedCardsSlugs?.length || 0}
        color="var(--c-nd-150)"
        onCancel={onCancel}
        button={
          <LoadingButton
            loading={openPreSealingDialog}
            startIcon={<Vault size={2} />}
            size="medium"
            color="secondary"
            onClick={() => setOpenPreSealingDialog(true)}
            disabled={
              !selectedCardsSlugs?.length ||
              openPreSealingDialog ||
              hasReachedMaxCardsPerBatch
            }
          >
            <FormattedMessage {...fantasy.seal} />
          </LoadingButton>
        }
      >
        <Vertical gap={0}>
          <LabelL bold>
            <FormattedMessage
              id="CardsSealingSummary.selectedCards"
              defaultMessage="{count} {count, plural, =0 {Cards} one {Card} other {Cards}} selected"
              values={{ count: selectedCardsSlugs?.length || 0 }}
            />
          </LabelL>
          {!!hasReachedMaxCardsPerBatch && (
            <LabelS color="var(--c-red-300)">
              <FormattedMessage
                id="CardsSealingSummary.selectedCards.overLimit"
                defaultMessage="You can only seal {count} cards at a time"
                values={{ count: maxCardsPerBatch }}
              />
            </LabelS>
          )}
        </Vertical>
      </SelectionSummary>
    </>
  );
};
